import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, signInWithEmailAndPassword, onAuthStateChanged, Auth, User, signOut as firebaseSignOut } from 'firebase/auth';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';
import { firebaseAuth, db } from '../firebase/firebase';
import { presentationAuth } from '../firebase/config';
import { useToast } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  currentUser: User | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  currentUser: null,
  signIn: async () => {},
  signOut: async () => {}
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if is in admin path - this affects auth behavior
  const isAdminPath = location.pathname.startsWith('/admin');
  
  useEffect(() => {
    console.log(`[AUTH] Current path: ${location.pathname}, isAdminPath: ${isAdminPath}`);
    
    // Skip auth checks for admin path - AdminPage has its own auth logic
    if (isAdminPath) {
      console.log('[AUTH] Admin path detected, skipping auth checks');
      setIsLoading(false);
      return;
    }
    
    // For non-admin paths, check localStorage for auth
    const storedName = localStorage.getItem('jimi_firstName');
    const storedAcknowledged = localStorage.getItem('jimi_acknowledged');
    
    if (storedName && storedAcknowledged === 'true') {
      console.log('[AUTH] User info found in localStorage, considering authenticated for presentation');
      setIsAuthenticated(true);
      
      // Only redirect if we're at the root path
      if (location.pathname === '/') {
        navigate('/login', { replace: true });
      }
    } else if (location.pathname !== '/login' && location.pathname !== '/') {
      // If not authenticated and not already at login, redirect to login
      console.log('[AUTH] No user info in localStorage, redirecting to login');
      navigate('/login', { replace: true, state: { from: location } });
    } else {
      // If already at login, just mark as not authenticated
      setIsAuthenticated(false);
    }
    
    setIsLoading(false);
    return;
  }, [navigate, toast, location, isAdminPath]);
  
  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      
      // Only use regular firebaseAuth for the presentation login
      console.log('[AUTH] Using firebase auth for regular login');
      await signInWithEmailAndPassword(firebaseAuth, email, password);
      
      // Auth state listener will handle the redirection
    } catch (error: any) {
      console.error('[AUTH] Sign in error:', error);
      toast({
        title: 'Sign In Failed',
        description: error.message || 'Failed to sign in. Please check your credentials.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    try {
      // Only sign out from regular auth for presentation
      if (firebaseAuth.currentUser) {
        await firebaseSignOut(firebaseAuth);
      }
      
      // Clear localStorage for presentation auth
      localStorage.removeItem('jimi_firstName');
      localStorage.removeItem('jimi_acknowledged');
      
      setIsAuthenticated(false);
      navigate('/login', { replace: true });
      
      toast({
        title: 'Signed Out',
        description: 'You have been successfully signed out.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error: any) {
      console.error('[AUTH] Sign out error:', error);
      toast({
        title: 'Sign Out Error',
        description: error.message || 'An error occurred during sign out.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, currentUser, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

export default AuthContext;
